// src/tattoo/lib/shopAvatar.js
//
// Helpers for uploading a shop's brand mark (logo/avatar) and writing
// the resulting URL into shops.avatar_url. The artist page hero
// already reads shop.avatar_url || shop.logo_url for the corner tile,
// so as soon as a value lands in that column the initials-on-gradient
// placeholder is replaced with the actual logo.
//
// Files live in the `shop-logos` Supabase Storage bucket — small
// dedicated bucket so we don't mix brand marks with flash images.
// Path convention: `{shop_id}/avatar.{ext}` — fixed filename so each
// upload overwrites the previous (upsert: true) rather than leaving
// orphans every time the artist changes their logo.

import { supabase } from "./supabaseClient";

const BUCKET = "shop-logos";

/**
 * Upload (or replace) the shop's avatar/logo and update shops.avatar_url.
 *
 * Returns the public URL on success, throws on failure.
 *
 * file: a File from an <input type="file"> or drop event.
 *
 * Implementation notes:
 *  - Path is `{shop_id}/avatar.{ext}` — same filename across uploads, so
 *    the storage object is overwritten rather than accumulating.
 *  - To avoid CDN/browser caching the old image when a new one is
 *    uploaded under the same path, we append `?v={timestamp}` to the
 *    persisted URL. The bucket itself is set with cache headers that
 *    favor freshness for logos.
 */
export async function uploadShopAvatar(shopId, file) {
  if (!shopId) throw new Error("shopId required");
  if (!file) throw new Error("file required");

  const ext = (file.name.split(".").pop() || "png").toLowerCase();
  const path = `${shopId}/avatar.${ext}`;

  // 1. Upload (overwrites any existing avatar at the same path).
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: "60",
      upsert: true,
      contentType: file.type || undefined,
    });
  if (upErr) {
    console.error("[shopAvatar] upload failed", upErr);
    throw upErr;
  }

  // 2. Resolve the public URL and cache-bust it so the browser doesn't
  // keep serving the previous logo from cache.
  const { data: urlData } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(path);
  const baseUrl = urlData?.publicUrl;
  if (!baseUrl) {
    throw new Error("Could not resolve public URL for uploaded logo.");
  }
  const url = `${baseUrl}?v=${Date.now()}`;

  // 3. Persist on the shop row. avatar_url is the column the artist
  // page hero corner tile already reads.
  const { error: dbErr } = await supabase
    .from("shops")
    .update({ avatar_url: url })
    .eq("id", shopId);
  if (dbErr) {
    console.error("[shopAvatar] shops update failed", dbErr);
    throw dbErr;
  }

  return url;
}

/**
 * Clear the shop's avatar_url. Doesn't delete the storage object —
 * cheap leaked space, and keeping the file around means re-uploading
 * the same file later is a single-object overwrite rather than a
 * fresh upload. Worth it for the lifecycle simplicity.
 */
export async function clearShopAvatar(shopId) {
  if (!shopId) throw new Error("shopId required");
  const { error } = await supabase
    .from("shops")
    .update({ avatar_url: null })
    .eq("id", shopId);
  if (error) {
    console.error("[shopAvatar] clear failed", error);
    throw error;
  }
}
